import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Bot, Clock, RefreshCw, User } from 'lucide-react';
import { useProjectActivity } from '../../../../../hooks/useProjectActivity';
import { useUserLookup } from '../../../../../hooks/useUserLookup';
/**
 * ActivityView — chronological feed of all project events.
 *
 * Shows system events (extraction runs), user events (status changes,
 * edits), and AI events (shanks agent outputs) in one unified stream.
 * Each entry is color-coded by actor type and filterable by category.
 *
 * This is the audit trail surface — critical for govcon accountability.
 */ export function ActivityView({ projectId }) {
    const { data, isLoading } = useProjectActivity(projectId, {
        pageSize: 100,
        refetchInterval: 10000
    });
    const userLookup = useUserLookup(projectId);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center py-16",
            children: /*#__PURE__*/ _jsx("div", {
                className: "h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500"
            })
        });
    }
    const entries = data?.entries ?? [];
    if (entries.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center rounded-lg border border-dashed border-slate-300 py-16 text-sm text-slate-500",
            children: "No activity recorded yet."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex items-baseline gap-2",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-sm font-semibold text-slate-900",
                        children: "Activity Log"
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs text-slate-500",
                        children: [
                            entries.length,
                            " events"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white",
                children: entries.map((entry)=>{
                    const actorType = entry.actorType || 'user';
                    const category = entry.category || '';
                    const ActorIcon = ACTOR_ICONS[actorType] || User;
                    const actorColor = ACTOR_COLORS[actorType] || 'text-slate-400 bg-slate-100';
                    const catLabel = CATEGORY_LABELS[category] || category;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-3 px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: `mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${actorColor}`,
                                children: /*#__PURE__*/ _jsx(ActorIcon, {
                                    className: "h-3.5 w-3.5"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-[13px] text-slate-800",
                                        children: [
                                            entry.actorUserId && /*#__PURE__*/ _jsx("span", {
                                                className: "font-semibold text-slate-900",
                                                children: userLookup.get(entry.actorUserId) || entry.actorUserId.slice(0, 8)
                                            }),
                                            !entry.actorUserId && actorType !== 'user' && /*#__PURE__*/ _jsx("span", {
                                                className: "font-semibold text-slate-900",
                                                children: actorType === 'system' ? 'System' : 'AI'
                                            }),
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-slate-600",
                                                children: entry.summary || entry.entryType
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(ChangeDetails, {
                                        detailsJson: entry.detailsJson
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400",
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Clock, {
                                                        className: "h-3 w-3"
                                                    }),
                                                    formatTimestamp(entry.at?.toDate())
                                                ]
                                            }),
                                            catLabel && /*#__PURE__*/ _jsx("span", {
                                                className: "rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-medium text-slate-500",
                                                children: catLabel
                                            }),
                                            entry.resourceType && /*#__PURE__*/ _jsx("span", {
                                                className: "text-slate-400",
                                                children: entry.resourceType
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }, entry.id);
                })
            })
        ]
    });
}
const ACTOR_ICONS = {
    user: User,
    system: RefreshCw,
    ai: Bot
};
const ACTOR_COLORS = {
    user: 'text-blue-600 bg-blue-50',
    system: 'text-slate-500 bg-slate-100',
    ai: 'text-purple-600 bg-purple-50'
};
const CATEGORY_LABELS = {
    extraction: 'Extraction',
    edit: 'Edit',
    review: 'Review',
    ai_output: 'AI',
    admin: 'Admin',
    reversion: 'Reverted'
};
/**
 * ChangeDetails renders before/after values from the audit details JSON.
 * Supports both single-field changes ({"field":"name","previous":"A","new":"B"})
 * and multi-field diffs ({"Name":{"from":"A","to":"B"},"Status":{"from":"X","to":"Y"}}).
 */ function ChangeDetails({ detailsJson }) {
    if (!detailsJson) return null;
    try {
        const data = JSON.parse(detailsJson);
        // Single-field: {"field":"name","previous":"old","new":"new"} or {"previousStatus":"X","newStatus":"Y"}
        if (data.previous !== undefined && data.new !== undefined) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "mt-1 flex items-center gap-1.5 text-[11px]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through",
                        children: String(data.previous || '(empty)')
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-slate-400",
                        children: "→"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700",
                        children: String(data.new)
                    })
                ]
            });
        }
        // Status change: {"previousStatus":"X","newStatus":"Y"}
        if (data.previousStatus !== undefined && data.newStatus !== undefined) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "mt-1 flex items-center gap-1.5 text-[11px]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through",
                        children: data.previousStatus || '(none)'
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-slate-400",
                        children: "→"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700",
                        children: data.newStatus
                    })
                ]
            });
        }
        // Multi-field diff: {"Name":{"from":"A","to":"B"}, ...}
        const diffEntries = Object.entries(data).filter(([, v])=>typeof v === 'object' && v !== null && 'from' in v && 'to' in v);
        if (diffEntries.length > 0) {
            return /*#__PURE__*/ _jsx("div", {
                className: "mt-1 space-y-0.5",
                children: diffEntries.map(([field, diff])=>{
                    const d = diff;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 text-[11px]",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-medium text-slate-500",
                                children: [
                                    field,
                                    ":"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through",
                                children: d.from || '(empty)'
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-slate-400",
                                children: "→"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700",
                                children: d.to
                            })
                        ]
                    }, field);
                })
            });
        }
    } catch  {
    // Invalid JSON — skip rendering details.
    }
    return null;
}
function formatTimestamp(date) {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
}
